import React, { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { FiBriefcase, FiSearch, FiRefreshCcw, FiPlus } from 'react-icons/fi'
import adminApi from '../../api/adminApi'
import Table from '../../components/Table/Table'
import StatusBadge from '../../components/StatusBadge/StatusBadge'
import Select from '../../components/Select/Select'
import Button from '../../components/Button/Button'
import './RecruiterJobs.css'

const RecruiterJobs = () => {
  const [jobs, setJobs] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [page, setPage] = useState(1)

  useEffect(() => {
    loadJobs()
  }, [page, statusFilter])

  const loadJobs = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page,
        limit: 25,
      }
      if (statusFilter !== 'all') {
        params.status = statusFilter
      }
      const response = await adminApi.get('/recruiter-jobs', { params })
      setJobs(response.data.data || response.data || [])
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load recruiter jobs')
      console.error('Error loading recruiter jobs:', err)
    } finally {
      setIsLoading(false)
    }
  }

  const filteredJobs = useMemo(() => {
    const jobsList = jobs || []
    if (!Array.isArray(jobsList)) return []
    const query = searchQuery.toLowerCase()
    return jobsList.filter((job) => {
      const matchesSearch =
        !query ||
        [
          job.title,
          job.companyName,
          job.location,
          ...(job.techStack || []),
        ]
          .filter(Boolean)
          .some((field) => String(field).toLowerCase().includes(query))
      
      const matchesStatus = statusFilter === 'all' || job.status === statusFilter
      
      return matchesSearch && matchesStatus
    })
  }, [jobs, searchQuery, statusFilter])

  const columns = [
    {
      key: 'title',
      label: 'Job Title',
      render: (row) => (
        <div>
          <strong>{row.title}</strong>
          <span className="recruiter-job-company">{row.companyName}</span>
        </div>
      ),
    },
    {
      key: 'location',
      label: 'Location',
      render: (row) => (
        <div>
          {row.location || '—'}
          {row.remoteType && (
            <div className="recruiter-job-remote">{row.remoteType}</div>
          )}
        </div>
      ),
    },
    {
      key: 'techStack',
      label: 'Tech Stack',
      render: (row) => (
        <div className="recruiter-job-tech">
          {row.techStack?.slice(0, 3).join(', ') || '—'}
          {row.techStack?.length > 3 && '...'}
        </div>
      ),
    },
    {
      key: 'employmentType',
      label: 'Type',
      render: (row) => row.employmentType || '—',
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => <StatusBadge status={row.status || 'open'} />,
    },
    {
      key: 'createdAt',
      label: 'Created',
      render: (row) => {
        if (!row.createdAt) return '—'
        return new Date(row.createdAt).toLocaleDateString()
      },
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <Link to={`/recruiter-jobs/${row._id}`} className="action-link">
          View Details
        </Link>
      ),
    },
  ]

  return (
    <div className="recruiter-jobs-page">
      <header className="page-header">
        <div>
          <p className="eyebrow">Recruitment Management</p>
          <h1>Recruiter Job Descriptions</h1>
          <p className="subhead">Manage job descriptions and requirements</p>
        </div>
        <div className="header-actions">
          <Button
            onClick={() => window.location.href = '/recruiter-jobs/new'}
          >
            <FiPlus size={18} />
            Create JD
          </Button>
          <Button
            variant="secondary"
            onClick={loadJobs}
            disabled={isLoading}
          >
            <FiRefreshCcw size={16} />
            Refresh
          </Button>
        </div>
      </header>

      <section className="toolbar">
        <div className="search-field">
          <FiSearch size={18} />
          <input
            type="search"
            placeholder="Search jobs"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <Select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
          options={[
            { value: 'all', label: 'All Status' },
            { value: 'open', label: 'Open' },
            { value: 'closed', label: 'Closed' },
            { value: 'archived', label: 'Archived' },
          ]}
        />
      </section>

      {error && (
        <div className="error-message" role="alert">
          {error}
        </div>
      )}

      <Table
        columns={columns}
        data={filteredJobs}
        loading={isLoading}
        emptyMessage="No recruiter jobs found"
      />
    </div>
  )
}

export default RecruiterJobs

